import React, { createContext, useContext, useState, useEffect } from 'react';
import client from '../api/client';

// Create the Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token and get user info
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  // Update local user state (e.g. after name change)
  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  };

  // Register function
  const register = async (email, password, confirmPassword, name) => {
    setLoading(true);
    setError(null);

    try {
      const response = await client.post('/api/auth/register', { email, password, confirmPassword, name });
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Login function
  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await client.post('/api/auth/login', { email, password });
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (err) {
      const message = err.response?.data?.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (token) {
        await client.post('/api/auth/logout', {}, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      setLoading(false);
    }
  };

  // Verify token function
  const verifyToken = async (token) => {
    try {
      const response = await client.get('/api/auth/verify', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setUser(response.data.user);
    } catch (err) {
      console.error('Token verification error:', err);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  // Get current user info
  const getCurrentUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const response = await client.get('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setUser(response.data.user);
      return response.data.user;
    } catch (err) {
      console.error('Get user error:', err);
      localStorage.removeItem('token');
      setUser(null);
      return null;
    }
  };

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    getCurrentUser,
    updateUser,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the Auth Context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;